import { Component, DestroyRef, Inject, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { ReportExportViewService } from '../../report-export-view.service';
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ViewTemplate, ViewTemplateCatalog } from '../../models/template.model';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';

import { MiqImagesModule } from '@miq/utils';

export interface SelectTemplateDialogInputData {
  reportId: number;
  viewId: number;
  selectedTemplateId?: number;
  templates: ViewTemplateCatalog;
  autoSelectTemplate?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ppt-select-template-dialog',
  standalone: true,
  templateUrl: './select-template-dialog.component.html',
  styleUrl: './select-template-dialog.component.scss',
  imports: [
    MatButton,
    MatDialogClose,
    MatIcon,
    L10nTranslationModule,
    MatDialogTitle,
    FormsModule,
    CdkScrollable,
    MatDialogContent,
    MatRadioGroup,
    ReactiveFormsModule,
    MatRadioButton,
    MiqImagesModule,
    MatDialogActions
],
})
export class SelectTemplateDialogComponent {
  public templatesCatalog: ViewTemplateCatalog;
  public templateFC: FormControl;
  public selectedTemplateImageSrc: string;

  private readonly reportId: number;
  private readonly viewId: number;
  private readonly currentlySelectedTempId: number;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public readonly dialogRef: MatDialogRef<SelectTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: SelectTemplateDialogInputData,
    private readonly reportExportViewService: ReportExportViewService,
    private readonly formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    if (data != null) {
      const firstOptionId = data.templates?.length > 0 ? data.templates[0].templateId : null;
      const selectedTemplateId = data.selectedTemplateId ?? firstOptionId;
      this.currentlySelectedTempId = data.selectedTemplateId;
      this.templateFC = this.formBuilder.control(selectedTemplateId, Validators.required);
      this.reportId = data.reportId;
      this.viewId = data.viewId;
      this.templatesCatalog = data.templates;
      this.setSelectedTemplateImageSrc(selectedTemplateId);
      this.setImageSrcChangeEvent();
      if (data.autoSelectTemplate) {
        this.onClose();
      }
    }
  }

  public selectTemplate() {
    const selectedTemplate = this.getSelectedTemplateFromCatalog(this.templateFC.value);
    this.reportExportViewService
      .saveTemplate(this.reportId, this.viewId, selectedTemplate)
      .subscribe(() => this.onClose(selectedTemplate.templateId));
  }

  public onClose(selectedTemplateId?: number): void {
    if (!selectedTemplateId) {
      const currentTemplate = this.getSelectedTemplateFromCatalog(this.templateFC.value) ?? { templateId: null };
      selectedTemplateId = currentTemplate.templateId;
    }
    const templateIdToEmit = selectedTemplateId === this.currentlySelectedTempId ? null : selectedTemplateId;
    this.dialogRef.close(templateIdToEmit);
  }

  private setImageSrcChangeEvent() {
    this.templateFC.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedTemplateId: number) => {
      this.setSelectedTemplateImageSrc(selectedTemplateId);
      this.cdr.markForCheck();
    });
  }

  private setSelectedTemplateImageSrc(selectedTemplateId: number) {
    const selectedTemplate = this.getSelectedTemplateFromCatalog(selectedTemplateId);
    this.selectedTemplateImageSrc = selectedTemplate?.imageUrl ?? '';
  }

  private getSelectedTemplateFromCatalog(selectedTemplateId: number): ViewTemplate {
    return this.templatesCatalog.find(template => template.templateId === selectedTemplateId);
  }
}
